export type UiTone = 'neutral' | 'accent' | 'info' | 'success' | 'warning' | 'danger'
export type UiSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
export type UiDensity = 'compact' | 'default' | 'comfortable'
export interface UiOption {
  value: string
  label: string
  description?: string
  icon?: import('vue').Component
  disabled?: boolean
  avatarUrl?: string | null
}
export function uiSearch(options: UiOption[], query: string) {
  const normalize = (text: string) =>
    text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('fr')
  const search = normalize(query.trim())
  return options.filter((option) =>
    normalize(`${option.label} ${option.description ?? ''}`).includes(search),
  )
}

// Appearance's schema accepts six-digit hex; use the same sRGB mix as the dark token.
export function uiAccentContrast(hex: string, dark = false) {
  const channels = [1, 3, 5].map((start) => parseInt(hex.slice(start, start + 2), 16) / 255)
  const rgb = channels.map((value) => (dark ? value * 0.74 + 0.26 : value))
  const linear = rgb.map((value) =>
    value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4,
  )
  const luminance = linear[0]! * 0.2126 + linear[1]! * 0.7152 + linear[2]! * 0.0722
  return (luminance + 0.05) / 0.05 >= 1.05 / (luminance + 0.05) ? '#000000' : '#ffffff'
}

// Solid accent controls keep white labels. Darken only their surface until WCAG AA.
export function uiAccentSolid(hex: string) {
  if (!/^#[\da-f]{6}$/i.test(hex)) return '#146fda'
  const channels = [1, 3, 5].map((start) => parseInt(hex.slice(start, start + 2), 16))
  for (let step = 0; step <= 100; step++) {
    const rgb = channels.map((channel) => Math.floor(channel * (1 - step / 100)))
    const linear = rgb.map((channel) => {
      const value = channel / 255
      return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
    })
    const luminance = linear[0]! * 0.2126 + linear[1]! * 0.7152 + linear[2]! * 0.0722
    if (1.05 / (luminance + 0.05) >= 4.5)
      return `#${rgb.map((value) => value.toString(16).padStart(2, '0')).join('')}`
  }
  return '#000000'
}
